/**
 * An event logger that sends information to the Premier Systems Ltd. event collector.
 *
 * You must set up a new application to receive events on the server as the application domain needs to be whitelisted
 * for CORS purposes.
 *
 * This logger will augment the event packets with useful device/browser information.
 */

import errorLoggerService from "@/app/errors/errorLoggerService";
import type {
  EventCollectorEventLogger,
  EventProperties,
  EventPropertyDecorator,
} from "@/app/eventlogging/eventCollectorEventLogger";

interface UploadEventsPayload {
  app: string;
  common?: EventProperties;
  events: EventProperties[];
}

export interface EventShipper {
  flush(maxWaitMs?: number): Promise<void>;
}

export class NoShipper implements EventShipper {
  flush() {
    return Promise.resolve();
  }
}

export class EventCollectorShipper implements EventShipper {
  private readonly baseUrl: string;
  private readonly appName: string;
  private readonly commonEventDecorators: EventPropertyDecorator[];
  private readonly maxBatchSizeForTransmit: number;

  private readonly logger: EventCollectorEventLogger;

  /**
   * Creates an event collector.
   *
   * @param appName The name of the applications (must be registered with the event collector).
   * @param commonEventDecorators Decorators that will add infrequently changed properties (flush time);
   * @param flushIntervalSecs Seconds between each flush attempt.
   * @param baseUrl The base url of the event collector
   */
  public constructor(
    appName: "brewman7" | "distributionapp",
    logger: EventCollectorEventLogger,
    commonEventDecorators: EventPropertyDecorator[] = [],
    flushIntervalSecs: number = 30,
    baseUrl: string = "https://events.premiersystems.com",
    maxBatchSizeForTransmit: number = 50
  ) {
    this.appName = appName;
    this.baseUrl = baseUrl;
    this.maxBatchSizeForTransmit = maxBatchSizeForTransmit;
    this.logger = logger;
    this.commonEventDecorators = commonEventDecorators;

    // Flush on navigate away
    // @Removing Unload event https://developer.mozilla.org/en-US/docs/Web/API/Window/unload_event#usage_notes
    window.addEventListener(
      "visibilitychange",
      async () => {
        if (document.visibilityState === "hidden") {
          await this.flush();
        }
      },
      false
    );

    // Flush periodically (recursive timer to ensure consistent spacing regardless of latency)
    const timerFunc = () => {
      setTimeout(async () => {
        await this.flush();
        timerFunc();
      }, 1000 * flushIntervalSecs);
    };

    timerFunc();
  }

  /**
   * Flush the buffer to the server. This will be called automatically at regular intervals.
   */
  public async flush(maxWaitMs?: number) {
    try {
      if (maxWaitMs && maxWaitMs > 0) {
        await Promise.race([
          new Promise((resolve) => setTimeout(resolve, maxWaitMs)),
          this.flushInternal(),
        ]);
      } else {
        await this.flushInternal();
      }
    } catch (e: any) {
      //
    }
  }

  private async flushInternal() {
    if (this.logger.queue.length === 0 || !navigator.onLine) {
      return;
    }

    // Create common properties from one time common
    let common = {};
    // With common decorators applied
    this.commonEventDecorators.forEach((decorator) => decorator(common));

    let eventsToPush = this.logger.queue.splice(
      0,
      this.maxBatchSizeForTransmit
    );

    while (eventsToPush.length > 0) {
      const uploadPayload: UploadEventsPayload = {
        app: this.appName,
        common,
        events: eventsToPush,
      };

      let result: Response | null = null;
      try {
        result = await fetch(`${this.baseUrl}/api/events`, {
          method: "POST",
          body: JSON.stringify(uploadPayload),
          keepalive: true, // Keep request alive after page unload
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });
        if (!result.ok) {
          throw new Error("Fetch Status Not OK");
        }
      } catch (e: any) {
        errorLoggerService.logError(
          `Failed to send event logger items. length: ${
            eventsToPush.length
          } device Id: ${eventsToPush[0]?.deviceId ?? "-"}
          status: ${result?.status ?? "-"}
          statusText: ${result?.statusText ?? "-"}`,
          e
        );

        // If we failed to send, prevent the queue from continuing to push till next flush
        return;
      }
      // continue to next batch of items (best effort - if this fails don't keep retrying that batch)
      eventsToPush = this.logger.queue.splice(0, this.maxBatchSizeForTransmit);
    }

    // This is best effort. It just means it was queued for submission - no guarantee it made it.
    this.logger.queue = [];
  }
}
